/**
 * Staff Console → Message Insights tab (2026-10-03, migration 0045).
 *
 * What the DAILY message audit job (campaigns worker, 13:13 UTC) found:
 *   - SKILL.md suggestions from the AI pass, each with the problem, the
 *     evidence (message ids), the proposed change and the patch. Accept /
 *     Dismiss / notes. Accept only records the decision and shows the patch
 *     to copy: nothing edits SKILL.md automatically (standing owner
 *     decision). A human applies an accepted patch in a normal PR.
 *   - Issues the deterministic detectors recorded, including the leads the
 *     job requeued on its own (auto_fixed). Dismiss / Resolve / Reopen.
 *   - The last runs (counts, AI status, cost).
 *
 * The live, on-demand scan stays in the Message Audit tab; this tab is the
 * persisted daily record. Visual idiom follows message-audit-panel.tsx.
 */

import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Copy,
  FileDiff,
  Lightbulb,
  Loader2,
  Wrench,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { isStaffUser } from './staff'
import {
  useMessageInsights,
  useReviewInsight,
  type InsightIssue,
  type InsightSuggestion,
} from './use-admin'

const SKILL_PATH = 'v3/workers/ai/src/prompts/skill.md'

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'bg-[rgba(248,113,113,0.12)] text-[#F87171]',
  high: 'bg-[rgba(248,113,113,0.12)] text-[#F87171]',
  warn: 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]',
  medium: 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]',
  info: 'bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]',
  low: 'bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]',
}

const STATUS_CLASS: Record<string, string> = {
  new: 'bg-[rgba(16,185,129,0.12)] text-[#34D399]',
  open: 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]',
  accepted: 'bg-[rgba(168,85,247,0.14)] text-[#D8B4FE]',
  auto_fixed: 'bg-[rgba(16,185,129,0.12)] text-[#34D399]',
  resolved: 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
  dismissed: 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
}

const PATCH_CHECK_LABEL: Record<string, { text: string; cls: string }> = {
  ok: { text: 'Patch lines match the current SKILL.md', cls: 'text-[#34D399]' },
  mismatch: { text: 'Some patch lines are not in the current SKILL.md, apply by hand', cls: 'text-[#FBBF24]' },
  no_anchor: { text: 'Additions only, place them under the named section', cls: 'text-[var(--color-silver)]' },
}

function Pill({ value, map }: { value: string; map: Record<string, string> }) {
  return (
    <Badge variant="outline" className={cn('border-none text-[10px] capitalize', map[value] ?? STATUS_CLASS.resolved)}>
      {value.replace('_', ' ')}
    </Badge>
  )
}

function fmtWhen(ts: string | null): string {
  if (!ts) return 'n/a'
  return `${ts.slice(0, 16).replace('T', ' ')} UTC`
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2 text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

function NotesEditor({
  initial,
  disabled,
  saving,
  onSave,
}: {
  initial: string | null
  disabled: boolean
  saving: boolean
  onSave: (notes: string | null) => void
}) {
  const [value, setValue] = useState(initial ?? '')
  const dirty = value !== (initial ?? '')
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        rows={2}
        maxLength={2000}
        placeholder="Notes for Eli / Taha (why accepted, what to change, ...)"
        className="min-h-[2.5rem] w-full flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs text-[var(--color-silver-bright)] placeholder:text-[var(--color-silver)] focus:outline-none focus:ring-2 focus:ring-[#34D399]"
      />
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        disabled={disabled || !dirty || saving}
        onClick={() => onSave(value.trim() ? value.trim() : null)}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save note'}
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Suggestion card
// ---------------------------------------------------------------------------

function SuggestionCard({ s, readOnly }: { s: InsightSuggestion; readOnly: boolean }) {
  const review = useReviewInsight()
  const [showPatch, setShowPatch] = useState(s.status === 'accepted')
  const pending = review.isPending
  const setStatus = (status: string) =>
    review.mutate(
      { kind: 'suggestions', id: s.id, status },
      { onSuccess: () => setShowPatch(status === 'accepted') },
    )
  const check = s.patch_check ? PATCH_CHECK_LABEL[s.patch_check] : null

  return (
    <div className="rounded-md border border-border bg-[rgba(255,255,255,0.02)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Pill value={s.severity} map={SEVERITY_CLASS} />
        <Pill value={s.status} map={STATUS_CLASS} />
        {s.category && (
          <span className="text-[10px] uppercase tracking-wide text-[var(--color-silver)]">{s.category.replace('_', ' ')}</span>
        )}
        <span className="ml-auto text-[10px] text-[var(--color-silver)]">{fmtWhen(s.created_at)}</span>
      </div>
      <p className="mt-2 text-sm font-medium text-[var(--color-silver-bright)]">{s.title}</p>
      <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <div>
          <p className="eyebrow text-[var(--color-silver)]">Problem</p>
          <p className="mt-0.5 text-[var(--color-silver-bright)]">{s.problem}</p>
        </div>
        <div>
          <p className="eyebrow text-[var(--color-silver)]">Proposed change{s.skill_section ? ` (${s.skill_section})` : ''}</p>
          <p className="mt-0.5 text-[var(--color-silver-bright)]">{s.proposed_change}</p>
        </div>
      </div>
      <p className="mt-2 break-all text-[10px] text-[var(--color-silver)]">
        Evidence: {s.evidence_message_ids.length} message(s) {s.evidence_message_ids.join(', ')}
        {s.evidence_lead_ids.length > 0 && <> · lead(s) {s.evidence_lead_ids.join(', ')}</>}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {s.status !== 'accepted' && (
          <Button size="sm" className="h-7 px-2 text-xs" disabled={readOnly || pending} onClick={() => setStatus('accepted')}>
            <Check className="mr-1 h-3 w-3" />
            Accept
          </Button>
        )}
        {s.status !== 'dismissed' && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={readOnly || pending} onClick={() => setStatus('dismissed')}>
            <X className="mr-1 h-3 w-3" />
            Dismiss
          </Button>
        )}
        {s.status !== 'new' && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={readOnly || pending} onClick={() => setStatus('new')}>
            Back to review
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setShowPatch((v) => !v)}>
          <FileDiff className="mr-1 h-3 w-3" />
          {showPatch ? 'Hide patch' : 'Show patch'}
        </Button>
        {pending && <Loader2 className="h-3 w-3 animate-spin text-[var(--color-silver)]" />}
        {review.isError && <span className="text-xs text-[#F87171]">{(review.error as Error).message}</span>}
      </div>

      {showPatch && (
        <div className="mt-3">
          {s.status === 'accepted' && (
            <p className="mb-2 rounded border border-[rgba(168,85,247,0.3)] bg-[rgba(168,85,247,0.08)] px-2 py-1.5 text-xs text-[#D8B4FE]">
              Accepted. SKILL.md has not been changed. Copy this patch into {SKILL_PATH} (and the dashboard
              skills-bundle copy) in a reviewed change.
            </p>
          )}
          <div className="mb-1 flex items-center justify-between gap-2">
            {check ? <span className={cn('text-[10px]', check.cls)}>{check.text}</span> : <span />}
            <CopyButton text={s.skill_patch} label="Copy patch" />
          </div>
          <pre className="max-h-80 overflow-auto rounded-md bg-[rgba(0,0,0,0.35)] p-2 text-[11px] leading-relaxed">
            {s.skill_patch.split('\n').map((line, i) => (
              <div
                key={i}
                className={cn(
                  'whitespace-pre-wrap',
                  line.startsWith('+') && 'text-[#34D399]',
                  line.startsWith('-') && 'text-[#F87171]',
                  line.startsWith('@@') && 'text-[#8FB4FA]',
                  !/^[+\-@]/.test(line) && 'text-[var(--color-silver)]',
                )}
              >
                {line || ' '}
              </div>
            ))}
          </pre>
        </div>
      )}

      <div className="mt-3">
        <NotesEditor
          key={s.notes ?? ''}
          initial={s.notes}
          disabled={readOnly}
          saving={pending}
          onSave={(notes) => review.mutate({ kind: 'suggestions', id: s.id, notes })}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Issue row
// ---------------------------------------------------------------------------

function IssueRow({ issue, readOnly }: { issue: InsightIssue; readOnly: boolean }) {
  const review = useReviewInsight()
  const [open, setOpen] = useState(false)
  const set = (status: string) => review.mutate({ kind: 'issues', id: issue.id, status })
  return (
    <>
      <TableRow>
        <TableCell>
          <Pill value={issue.severity} map={SEVERITY_CLASS} />
        </TableCell>
        <TableCell>
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-start gap-1 text-left">
            <ChevronDown className={cn('mt-0.5 h-3 w-3 shrink-0 text-[var(--color-silver)] transition-transform', !open && '-rotate-90')} />
            <span className="text-sm text-[var(--color-silver-bright)]">{issue.title}</span>
          </button>
          <div className="ml-4 text-[10px] text-[var(--color-silver)]">
            {issue.type.replace(/_/g, ' ')}
            {issue.fixed_at && ` · requeued ${fmtWhen(issue.fixed_at)}`}
            {issue.status === 'open' && issue.fix_action === 'requeue' && ' · still waiting after an auto-fix, needs a human'}
          </div>
        </TableCell>
        <TableCell className="text-xs">{issue.owner_email ?? 'n/a'}</TableCell>
        <TableCell>
          <Pill value={issue.status} map={STATUS_CLASS} />
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">{issue.occurrences}</TableCell>
        <TableCell className="text-xs">{fmtWhen(issue.last_seen_at)}</TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1">
            {issue.status !== 'dismissed' && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={readOnly || review.isPending} onClick={() => set('dismissed')}>
                Dismiss
              </Button>
            )}
            {(issue.status === 'open' || issue.status === 'auto_fixed') && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={readOnly || review.isPending} onClick={() => set('resolved')}>
                Resolve
              </Button>
            )}
            {(issue.status === 'dismissed' || issue.status === 'resolved') && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={readOnly || review.isPending} onClick={() => set('open')}>
                Reopen
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="bg-[rgba(255,255,255,0.015)]">
            <div className="space-y-2 py-1 text-xs">
              <p className="break-all text-[var(--color-silver)]">
                First seen {fmtWhen(issue.first_seen_at)}
                {issue.lead_id && ` · lead ${issue.lead_id}`}
                {issue.campaign_id && ` · campaign ${issue.campaign_id}`}
                {issue.message_ids.length > 0 && ` · messages ${issue.message_ids.join(', ')}`}
              </p>
              {issue.detail && (
                <pre className="max-h-48 overflow-auto rounded bg-[rgba(0,0,0,0.3)] p-2 text-[10px] text-[var(--color-silver)]">
                  {JSON.stringify(issue.detail, null, 2)}
                </pre>
              )}
              <NotesEditor
                key={issue.notes ?? ''}
                initial={issue.notes}
                disabled={readOnly}
                saving={review.isPending}
                onSave={(notes) => review.mutate({ kind: 'issues', id: issue.id, notes })}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function SummaryCard({ label, value, hint, alert }: { label: string; value: string | number; hint?: string; alert?: boolean }) {
  return (
    <Card className="flex-1">
      <CardContent className="p-4">
        <p className="eyebrow text-[var(--color-silver)]">{label}</p>
        <p className={cn('mt-1 text-2xl font-bold tracking-tight', alert ? 'text-[#F87171]' : 'text-[var(--color-silver-bright)]')}>
          {value}
        </p>
        {hint && <p className="mt-0.5 text-xs text-[var(--color-silver)]">{hint}</p>}
      </CardContent>
    </Card>
  )
}

export function MessageInsightsPage() {
  const { user } = useUser()
  const readOnly = !isStaffUser(user?.id)
  const [allSuggestions, setAllSuggestions] = useState(false)
  const [allIssues, setAllIssues] = useState(false)
  const { data, isLoading, isError, error } = useMessageInsights({ allIssues, allSuggestions })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading message insights...
      </div>
    )
  }
  if (isError || !data) {
    return <p className="py-10 text-sm text-[#F87171]">Failed to load: {(error as Error | null)?.message ?? 'unknown error'}</p>
  }

  const s = data.summary
  const lastRun = data.runs[0]
  return (
    <div className="space-y-4">
      <p className="text-xs text-[var(--color-silver)]">
        A daily job (13:13 UTC) scans every account's messages, requeues replies that are safe to retry, records
        everything else, and asks the AI for SKILL.md improvements backed by real messages. Nothing here changes
        SKILL.md: accepting a suggestion only marks it accepted and shows the patch to copy.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row">
        <SummaryCard label="Suggestions to review" value={s?.new_suggestions ?? 0} hint={`${s?.accepted_suggestions ?? 0} accepted, not yet applied`} />
        <SummaryCard label="Open issues" value={s?.open_issues ?? 0} hint={`${s?.open_critical ?? 0} critical`} alert={(s?.open_critical ?? 0) > 0} />
        <SummaryCard label="Auto-fixed (7d)" value={s?.auto_fixed_7d ?? 0} hint="replies requeued by the job" />
        <SummaryCard
          label="Last run"
          value={lastRun ? fmtWhen(lastRun.started_at).replace(' UTC', '') : 'none yet'}
          hint={lastRun ? `AI: ${lastRun.ai_status ?? 'n/a'} · $${Number(s?.ai_cost_today_usd ?? 0).toFixed(3)} today` : 'runs daily at 13:13 UTC'}
        />
      </div>

      {/* Suggestions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
            <Lightbulb className="h-4 w-4 text-[#34D399]" />
            SKILL.md suggestions
          </CardTitle>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAllSuggestions((v) => !v)}>
            {allSuggestions ? 'Hide dismissed' : 'Show dismissed'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.suggestions.length === 0 ? (
            <p className="py-2 text-sm text-[var(--color-silver)]">No suggestions waiting. The next daily run will add any it finds.</p>
          ) : (
            data.suggestions.map((sug) => <SuggestionCard key={sug.id} s={sug} readOnly={readOnly} />)
          )}
        </CardContent>
      </Card>

      {/* Issues */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
            <AlertTriangle className="h-4 w-4 text-[#FBBF24]" />
            Issues found
          </CardTitle>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAllIssues((v) => !v)}>
            {allIssues ? 'Open and auto-fixed only' : 'Show resolved and dismissed'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Severity</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Seen</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.issues.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-sm text-[#34D399]">
                      Nothing open. All clear.
                    </TableCell>
                  </TableRow>
                )}
                {data.issues.map((i) => (
                  <IssueRow key={i.id} issue={i} readOnly={readOnly} />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Runs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
            <Wrench className="h-4 w-4 text-[var(--color-silver)]" />
            Recent runs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead className="text-right">Issues</TableHead>
                  <TableHead className="text-right">New</TableHead>
                  <TableHead className="text-right">Auto-fixed</TableHead>
                  <TableHead className="text-right">Resolved</TableHead>
                  <TableHead className="text-right">Sampled</TableHead>
                  <TableHead className="text-right">Suggestions</TableHead>
                  <TableHead>AI</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="py-6 text-center text-sm text-[var(--color-silver)]">
                      No runs yet.
                    </TableCell>
                  </TableRow>
                )}
                {data.runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">
                      {fmtWhen(r.started_at)}
                      {!r.finished_at && <span className="ml-1 text-[#FBBF24]">(running or crashed)</span>}
                      {r.error && <div className="text-[10px] text-[#F87171]">{r.error.slice(0, 120)}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{r.trigger}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.issues_found}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.issues_new}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.auto_fixed}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.issues_resolved}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.conversations_sampled}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.suggestions_created}</TableCell>
                    <TableCell className="text-xs">{r.ai_status ?? 'n/a'}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">${Number(r.ai_cost_usd ?? 0).toFixed(3)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
